/**
 * PreviewOverlay -- 전체화면 미리보기 오버레이
 *
 * 에디터 위에 오버레이를 띄워 mode="viewer" 청첩장을 모바일 뷰포트처럼 표시.
 * ESC / 닫기 버튼 / 배경 클릭으로 닫기.
 */

import { useEffect, useCallback } from 'react'
import { X } from 'lucide-react'
import { DEFAULT_TEMPLATE_CONFIG } from '@cugumi/shared/schemas'
import type { TemplateConfig } from '@cugumi/shared/schemas'
import type { FlatInvitation } from '../../stores/invitationStore.js'
import { getTemplateComponent } from '../templates/index.js'
import styles from './PreviewOverlay.module.css'

interface PreviewOverlayProps {
  invitation: FlatInvitation
  templateConfig?: TemplateConfig
  onClose: () => void
}

export function PreviewOverlay({ invitation, templateConfig, onClose }: PreviewOverlayProps) {
  const config = templateConfig ?? DEFAULT_TEMPLATE_CONFIG
  const TemplateComponent = getTemplateComponent(config.componentId)

  // ESC 키로 닫기
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // body 스크롤 잠금
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  // 배경 클릭으로 닫기 (container 바깥 클릭)
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose()
    },
    [onClose],
  )

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.container}>
        <TemplateComponent
          invitation={invitation}
          config={config}
          mode="viewer"
        />
      </div>
      <button
        type="button"
        className={styles.closeButton}
        onClick={onClose}
        aria-label="미리보기 닫기"
      >
        <X size={20} />
      </button>
    </div>
  )
}
